/**
 * components/profile/ProfileReadinessCard.jsx — v3.7.10 Phase 84.31 Part 4
 *
 * Replaces the previous stack of overlapping completion signals
 * ("Complete your profile to unlock more personalised insights",
 * "0 of 6 planning areas completed", the tier badge, and the raw
 * percentage) with the two clean concepts the redesign spec asked
 * for:
 *
 *   • Data coverage      — "2120 can see most of your regular
 *                          financial activity" (based on real accounts
 *                          + recent transactions).
 *   • Planning readiness — "Confirm your tax residency to enable
 *                          country-specific guidance" (based on
 *                          missing critical facts).
 *
 * Neither is styled as a "score" or "%". They are described in words.
 * The card gracefully degrades when the summary payload hasn't arrived
 * yet or is missing either field (older backend).
 */
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Eye, Compass, CheckCircle2, ArrowRight } from 'lucide-react';

const COVERAGE_ICON = { starting: Eye, basics: Eye, complete: CheckCircle2 };

export default function ProfileReadinessCard({ summary, loading }) {
  const [, setSearchParams] = useSearchParams();

  if (loading) {
    return (
      <Card className="rounded-2xl border-border/70" data-testid="profile-readiness-loading">
        <CardContent className="p-5 space-y-3">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-2/3" />
        </CardContent>
      </Card>
    );
  }

  const coverage  = summary?.data_coverage;
  const readiness = summary?.planning_readiness;
  if (!coverage && !readiness) return null;

  const CovIcon = COVERAGE_ICON[coverage?.level] || Eye;

  const jumpToGap = (gap) => {
    if (!gap) return;
    if (gap.tab === 'location') {
      setSearchParams({ tab: 'location' }, { replace: false });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const el = document.querySelector(`[data-lp-field="${gap.key}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      try { el.focus({ preventScroll: true }); } catch { /* non-focusable ok */ }
    }
  };

  return (
    <Card className="rounded-2xl border-border/70" data-testid="profile-readiness-card">
      <CardContent className="p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {/* Data coverage */}
          {coverage && (
            <div data-testid="profile-readiness-coverage">
              <div className="flex items-center gap-2 mb-1.5">
                <CovIcon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">
                  Data coverage
                </span>
              </div>
              <p className="text-sm font-medium text-charcoal leading-snug">
                {coverage.label}
              </p>
              {coverage.hint && (
                <p className="text-[12px] text-muted-foreground mt-1 leading-relaxed">
                  {coverage.hint}
                </p>
              )}
            </div>
          )}
          {/* Planning readiness */}
          {readiness && (
            <div data-testid="profile-readiness-planning">
              <div className="flex items-center gap-2 mb-1.5">
                <Compass className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">
                  Planning readiness
                </span>
              </div>
              <p
                className={
                  'text-sm font-medium leading-snug ' +
                  (readiness.ready ? 'text-emerald-700 dark:text-emerald-400' : 'text-charcoal')
                }
              >
                {readiness.label}
              </p>
              {readiness.next_step && (
                <button
                  type="button"
                  onClick={() => jumpToGap(readiness.next_step)}
                  className="text-primary text-[12px] hover:underline mt-1.5 inline-flex items-center gap-1"
                  data-testid={`profile-readiness-next-step-${readiness.next_step.key}`}
                >
                  Fix this next <ArrowRight className="h-3 w-3" aria-hidden="true" />
                </button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
